import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GTMService } from '../../../shared/gtm.service';
import { isIFrameSameOrigin } from '../../../shared/helpers/is-same-origin';
import { NotificationService } from '../../../shared/notification.service';
import { OeAppPersonService } from '../../../shared/oe-app-person.service';
import { OeAppService } from '../../../shared/oe-app.service';
import { WindowService } from '../../../shared/window.service';
import { Pages } from '../../../shared/types/deep-linking';
import {
  PRIVATE_CLASS_EVENT,
  PrivateClassBannerEventType,
  ReservationLockReason,
} from '../../../shared/types/live-class-events.enum';
import { LiveClassEventService } from '../../live-class-event.service';
import { LiveClassEventDto } from '../../live-class-event-dto';

export interface PrivateClassBannerCancelPayload {
  reservationId: number;
  dateTimeLabel: string;
  lessonDisplayName?: string;
}

const MAX_BANNER_HEIGHT_PX = 2000;

@Component({
  selector: 'lp2-pc-banner-iframe',
  template: `
    <iframe #bannerFrame lp2UserwaySync class="border-0 w-100 pc-banner-iframe" [src]="url | safeUrl"></iframe>
  `,
  styles: [
    `
      .pc-banner-iframe {
        height: 460px;
        margin-bottom: 2rem;
      }
      @media (max-width: 1200px) {
        .pc-banner-iframe {
          height: 650px;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PcBannerIframeComponent implements OnInit, OnDestroy {
  @Output() reserve = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<PrivateClassBannerCancelPayload>();
  @ViewChild('bannerFrame') bannerFrame: ElementRef<HTMLIFrameElement>;

  public url: string;
  private boundHandler: (event: MessageEvent) => void;
  private liveClassEventSubscription: Subscription;

  constructor(
    private oeApp: OeAppService,
    private oeAppPersonService: OeAppPersonService,
    private gtmService: GTMService,
    private liveClassEventService: LiveClassEventService,
    private windowService: WindowService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.url =
      `${this.oeApp.webPrivateClassesBannerHost}` +
      `?env=${this.oeApp.environment}` +
      `&lang=${this.oeAppPersonService.preferredLang}`;

    this.boundHandler = this.handleEvents.bind(this);
    window.addEventListener('message', this.boundHandler, false);

    this.liveClassEventSubscription = this.liveClassEventService.onChange.subscribe((event: LiveClassEventDto) =>
      this.handleLiveClassEvent(event)
    );
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.boundHandler);
    if (this.liveClassEventSubscription) {
      this.liveClassEventSubscription.unsubscribe();
    }
  }

  public postRefetch() {
    this.postToBanner(PrivateClassBannerEventType.REFETCH_STATE_EVENT);
  }

  public postLocked(reason: ReservationLockReason | null) {
    this.postToBanner(PrivateClassBannerEventType.LOCKED_EVENT, reason);
  }

  public handleEvents(event: MessageEvent) {
    const { data: message, origin } = event;

    if (!isIFrameSameOrigin(this.oeApp.webPrivateClassesBannerHost, origin)) {
      return;
    }

    switch (message.type) {
      case PrivateClassBannerEventType.ON_RESERVE_EVENT:
        this.reserve.emit();
        break;
      case PrivateClassBannerEventType.ON_CANCEL_EVENT:
        if (message.data && message.data.reservationId != null) {
          this.cancel.emit(message.data);
        }
        break;
      case PrivateClassBannerEventType.ON_CHECK_IN_EVENT:
        this.liveClassEventService.triggerEvent('REFETCH_LIVE_CLASSES', null);
        break;
      // the banner opens the classroom itself and tracks the click on its own event;
      // all we owe it is deleting the class reminder it points at
      case PrivateClassBannerEventType.ON_JOIN_CLASS_EVENT:
        if (message.data && message.data.notificationId) {
          this.clearClassReminder(message.data.notificationId);
        }
        break;
      case PrivateClassBannerEventType.PC_BANNER_TRACKING_EVENT:
        this.gtmService.sendGA4Event(message.data);
        break;
      // the banner reports its rendered height; the fixed CSS heights only cover the first paint
      case PrivateClassBannerEventType.ON_RESIZE_EVENT:
        if (message.data && this.isValidBannerHeight(message.data.height)) {
          this.resizeBannerFrame(message.data.height);
        }
        break;
      // "See all", the lessons title and a lesson row all open the full PC page,
      // which owns the tracks/lessons routing inside its own iframe app.
      case PrivateClassBannerEventType.ON_SEE_ALL_EVENT:
      case PrivateClassBannerEventType.ON_PRESS_LESSONS_EVENT:
      case PrivateClassBannerEventType.ON_SELECT_LESSON_EVENT:
        this.windowService.redirectToHref(Pages.PRIVATE_CLASSES);
        break;
      case PrivateClassBannerEventType.ON_PRESS_FEEDBACK_EVENT:
        if (message.data && message.data.feedbackUUID) {
          this.windowService.redirectToHref(Pages.PRIVATE_CLASSES);
        }
        break;
    }
  }

  // lock the banner while a group-class signup confirmation (pre-check) is pending, mirroring oe-app
  private handleLiveClassEvent(event: LiveClassEventDto) {
    if (!event) {
      return;
    }
    switch (event.event) {
      case 'SIGNUP_CONFIRM':
        this.postLocked(ReservationLockReason.CONCURRENT_REQUEST);
        break;
      case 'SIGNUP_CANCEL':
        this.postLocked(null);
        break;
      case 'SIGNED_UP':
        this.postRefetch();
        break;
    }
  }

  // a failed delete must not break the join the banner already performed
  private clearClassReminder(notificationId: number) {
    this.notificationService
      .setDeleteFlag(notificationId)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private isValidBannerHeight(height: any): height is number {
    return typeof height === 'number' && isFinite(height) && height > 0 && height <= MAX_BANNER_HEIGHT_PX;
  }

  private resizeBannerFrame(height: number) {
    const frame = this.bannerFrame && this.bannerFrame.nativeElement;
    if (frame) {
      frame.style.height = `${Math.ceil(height)}px`;
    }
  }

  private postToBanner(type: PrivateClassBannerEventType, data?: any) {
    const frame = this.bannerFrame && this.bannerFrame.nativeElement;
    if (frame && frame.contentWindow) {
      const targetOrigin = new URL(this.oeApp.webPrivateClassesBannerHost).origin;
      frame.contentWindow.postMessage({ type: PRIVATE_CLASS_EVENT, data: { type, data } }, targetOrigin);
    }
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/live-class-revamp/pc-banner-iframe/pc-banner-iframe.component.ts